/**
 * LocationPickerMap - Interactive map for selecting a location
 * Issue #1081: Location picker for events
 *
 * Uses MapLibre GL JS with Stadia Alidade Smooth Dark tiles.
 * Supports draggable marker for precise location selection.
 */

import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';

interface LocationPickerMapProps {
  /** Current selected location */
  location: { lat: number; lng: number } | null;
  /** Callback when location changes (marker dragged or map clicked) */
  onLocationChange: (location: { lat: number; lng: number }) => void;
  /** Map container height */
  height?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get Stadia Maps API key based on current domain
 * Reused from RouteMap.tsx
 */
function getStadiaApiKey(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.includes('oro.run')) return import.meta.env.VITE_STADIA_API_KEY_RUN ?? '';
  if (host.includes('oro.bike')) return import.meta.env.VITE_STADIA_API_KEY_BIKE ?? '';
  return import.meta.env.VITE_STADIA_API_KEY_FIT ?? '';
}

/**
 * Build Stadia map style specification
 * Uses Alidade Smooth Dark theme to match ORO Nightfall design
 */
function buildMapStyle(): maplibregl.StyleSpecification {
  const apiKey = getStadiaApiKey();
  const keyParam = apiKey ? `?api_key=${apiKey}` : '';

  return {
    version: 8,
    sources: {
      'stadia-tiles': {
        type: 'raster',
        tiles: [
          `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}.png${keyParam}`,
        ],
        tileSize: 256,
        attribution:
          '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      },
    },
    layers: [
      {
        id: 'stadia-tiles-layer',
        type: 'raster',
        source: 'stadia-tiles',
        minzoom: 0,
        maxzoom: 20,
      },
    ],
  };
}

// ORO accent color for marker
const MARKER_COLOR = '#E1C37A'; // --oro-gold

// Default center (US center) if no location provided
const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283];
const DEFAULT_ZOOM = 3;
const LOCATION_ZOOM = 15;

export default function LocationPickerMap({
  location,
  onLocationChange,
  height = '300px',
  className = '',
}: LocationPickerMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);

  // Create or update marker
  const updateMarker = useCallback(
    (lngLat: { lng: number; lat: number }) => {
      if (!map.current) return;

      if (marker.current) {
        marker.current.setLngLat([lngLat.lng, lngLat.lat]);
      } else {
        // Create marker element
        const markerEl = document.createElement('div');
        markerEl.style.cssText = `
          width: 32px;
          height: 32px;
          background-color: ${MARKER_COLOR};
          border: 3px solid white;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
          cursor: grab;
        `;

        marker.current = new maplibregl.Marker({
          element: markerEl,
          draggable: true,
          anchor: 'bottom',
        })
          .setLngLat([lngLat.lng, lngLat.lat])
          .addTo(map.current);

        // Handle drag end
        marker.current.on('dragend', () => {
          const newLngLat = marker.current?.getLngLat();
          if (newLngLat) {
            onLocationChange({ lat: newLngLat.lat, lng: newLngLat.lng });
          }
        });
      }
    },
    [onLocationChange]
  );

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;
    if (map.current) return;

    const initialCenter = location
      ? [location.lng, location.lat] as [number, number]
      : DEFAULT_CENTER;
    const initialZoom = location ? LOCATION_ZOOM : DEFAULT_ZOOM;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: buildMapStyle(),
      center: initialCenter,
      zoom: initialZoom,
      attributionControl: false,
    });

    // Add navigation controls
    map.current.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'top-right'
    );

    // Add attribution (collapsed)
    const attributionControl = new maplibregl.AttributionControl({ compact: true });
    map.current.addControl(attributionControl, 'bottom-right');

    // Collapse attribution by default
    map.current.on('load', () => {
      const attrButton = mapContainer.current?.querySelector(
        '.maplibregl-ctrl-attrib-button'
      ) as HTMLButtonElement;
      if (attrButton) {
        attrButton.click();
      }

      // Add initial marker if location exists
      if (location) {
        updateMarker({ lat: location.lat, lng: location.lng });
      }
    });

    // Handle map click to place/move marker
    map.current.on('click', (e) => {
      const { lng, lat } = e.lngLat;
      updateMarker({ lat, lng });
      onLocationChange({ lat, lng });
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        marker.current = null;
      }
    };
  }, []);

  // Update marker when location prop changes externally (e.g., from search)
  useEffect(() => {
    if (!map.current || !location) return;

    updateMarker(location);

    // Fly to new location
    map.current.flyTo({
      center: [location.lng, location.lat],
      zoom: LOCATION_ZOOM,
      duration: 1000,
    });
  }, [location?.lat, location?.lng, updateMarker]);

  return (
    <div
      ref={mapContainer}
      className={`rounded-lg overflow-hidden border border-oro-border ${className}`}
      style={{ height }}
    />
  );
}
